import { useAuth } from "./useAuth";
import { useQuery } from "react-query";
import { getRentalApplication, getRentalApplicants } from "@/services/api";

interface UseRentalApplicationOptions {
  redirectOnUnauth?: boolean;
}

export function useRentalApplication({
  redirectOnUnauth = true,
}: UseRentalApplicationOptions = {}) {
  const { user } = useAuth({ redirectOnUnauth });

  const canFetch = Boolean(user?.application_id && user?.application_type === "rental");
  const {
    data: rentalApplication,
    refetch: refetchApplication,
    isLoading: rentalApplicationLoading,
  } = useQuery("rentalApplication", () => getRentalApplication(user?.application_id as string), {
    refetchOnWindowFocus: false,
    retry: false,
    enabled: canFetch,
  });

  const {
    data: rentalApplicants,
    refetch: refetchApplicants,
    isLoading: rentalApplicantLoading,
  } = useQuery("rentalApplicants", () => getRentalApplicants(user?.application_id as string), {
    refetchOnWindowFocus: false,
    retry: false,
    enabled: canFetch,
  });

  const refetchAll = () => {
    refetchApplication();
    refetchApplicants();
  };

  const userApplicant = rentalApplicants?.find((applicant) => applicant.user_id === user?.id);

  // primary applicant first, then secondary applicants sorted by first name and last name
  rentalApplicants?.sort((a, b) => {
    if (a.primary) return -1;
    if (b.primary) return 1;
    return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
  });
  return {
    rentalApplication,
    rentalApplicants,
    userApplicant,
    refetchAll,
    refetchApplicants,
    refetchApplication,
    isLoading: rentalApplicationLoading || rentalApplicantLoading,
  };
}
